import { useCallback, useMemo, useRef, useState } from 'react';
import { pageFromStorageKey } from '../../utils/cardGridLayout';
import { useCardGridLayout } from '../../context/CardGridLayoutContext';
import { useCardOrder } from '../../hooks/useCardOrder';
import './SortableCardGrid.css';

function DragHandle({ onDragStart, onDragEnd }) {
  return (
    <span
      className="sortable-card-grid__handle"
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      title="Drag to reorder"
      aria-label="Drag to reorder"
    >
      <svg width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden="true">
        <circle cx="2" cy="2" r="1.5" fill="currentColor" />
        <circle cx="8" cy="2" r="1.5" fill="currentColor" />
        <circle cx="2" cy="8" r="1.5" fill="currentColor" />
        <circle cx="8" cy="8" r="1.5" fill="currentColor" />
        <circle cx="2" cy="14" r="1.5" fill="currentColor" />
        <circle cx="8" cy="14" r="1.5" fill="currentColor" />
      </svg>
    </span>
  );
}

function SortableCardItem({
  item,
  itemClassName,
  dragId,
  overId,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onDragLeave,
}) {
  const itemRef = useRef(null);

  const handleHandleDragStart = (event) => {
    onDragStart(event, item.id, itemRef.current);
  };

  return (
    <div
      ref={itemRef}
      className={`sortable-card-grid__item${itemClassName ? ` ${itemClassName}` : ''}${
        dragId === item.id ? ' sortable-card-grid__item--dragging' : ''
      }${overId === item.id && dragId !== item.id ? ' sortable-card-grid__item--over' : ''}`}
      onDragOver={(event) => onDragOver(event, item.id)}
      onDrop={(event) => onDrop(event, item.id)}
      onDragLeave={onDragLeave}
    >
      <DragHandle onDragStart={handleHandleDragStart} onDragEnd={onDragEnd} />
      <div className="sortable-card-grid__content">{item.node}</div>
    </div>
  );
}

export default function SortableCardGrid({
  storageKey,
  className = '',
  items,
  itemClassName = '',
  section = 'kpi',
}) {
  const page = pageFromStorageKey(storageKey);
  const { getGridStyle, getDensityClass } = useCardGridLayout(page);
  const defaultIds = useMemo(() => items.map((item) => item.id), [items]);
  const { sortItems, moveCard } = useCardOrder(storageKey, defaultIds);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const orderedItems = useMemo(() => sortItems(items), [items, sortItems]);
  const gridStyle = getGridStyle(section);
  const densityClass = getDensityClass(section);

  const handleDragStart = useCallback((event, id, dragImageEl) => {
    setDragId(id);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', id);
    if (dragImageEl) {
      event.dataTransfer.setDragImage(dragImageEl, 24, 24);
    }
  }, []);

  const handleDragOver = useCallback((event, id) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setOverId(id);
  }, []);

  const handleDrop = useCallback(
    (event, id) => {
      event.preventDefault();
      const fromId = event.dataTransfer.getData('text/plain') || dragId;
      if (fromId && fromId !== id) {
        moveCard(fromId, id);
      }
      setDragId(null);
      setOverId(null);
    },
    [dragId, moveCard]
  );

  const handleDragEnd = useCallback(() => {
    setDragId(null);
    setOverId(null);
  }, []);

  const handleDragLeave = useCallback((itemId) => {
    setOverId((prev) => (prev === itemId ? null : prev));
  }, []);

  return (
    <section
      className={`sortable-card-grid ${densityClass} ${className}`.trim()}
      style={gridStyle}
      aria-label="Draggable cards"
    >
      {orderedItems.map((item) => (
        <SortableCardItem
          key={item.id}
          item={item}
          itemClassName={itemClassName}
          dragId={dragId}
          overId={overId}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onDragEnd={handleDragEnd}
          onDragLeave={() => handleDragLeave(item.id)}
        />
      ))}
    </section>
  );
}
